import { useTranslation, Trans } from 'react-i18next';
import { Dropdown } from 'primereact/dropdown';
import { Outlet, Link, useLocation } from 'react-router-dom';
import {
  LayoutDashboard, ShoppingCart, LogOut, Menu, Sun, Moon, ChevronLeft,
  Shield, FlaskConical, MapPin, AlertOctagon, ExternalLink, Home, ChevronRight,
  UtensilsCrossed, Bell, X, Lock, Unlock, Search, Clock, MessageCircle,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { CommandPalette } from '@/components/layout/CommandPalette';
import { RecordViewerProvider } from '@/components/records/RecordViewer';
import { useAuthStore } from '@/stores/auth.store';
import { useThemeStore } from '@/stores/theme.store';
import { readableBrandColor } from '@/lib/brand-contrast';
import { useUiStore } from '@/stores/ui.store';
import { useLogout } from '@/hooks/useAuth';
import { useState, useMemo, useEffect, useCallback } from 'react';
import { cn, formatDateOnly } from '@/lib/utils';
import { getTrialPhaseState } from '@/lib/trial';
import { EmailVerifyBanner } from '@/components/layout/EmailVerifyBanner';
import type { DunningStatus } from '@/lib/billing';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '@/lib/api';
import { useLocationStore, type LocationOption } from '@/stores/location.store';
import { useSocketEvent } from '@/hooks/useSocketEvent';
import { LanguageSwitcher } from '@/components/ui/language-switcher';
import {
  PINNED_TOP, PINNED_REPORTS, PINNED_SYSTEM,
  visibleWorkspaces, getFirstAccessibleTab, getWorkspaceByPath, matchesPermission,
  visibleTabs,
  type PinnedEntry,
} from '@/config/navigation';
import { useScreenDocumentTitle } from '@/hooks/useScreenDocumentTitle';

interface SidebarWorkspace {
  id: string;
  labelKey: string;
  icon: React.ElementType;
  basePath: string;
  href: string;
}

/** Location row fields the layout's switcher reads from GET /locations. */
type LayoutLocation = Pick<LocationOption, 'id' | 'name' | 'isDefault'>;

/** Build the flat, permission-filtered workspace list for the sidebar. */
function useSidebarWorkspaces(
  hasPermission: (p: string) => boolean,
  permissionsKey: unknown,
): SidebarWorkspace[] {
  return useMemo(
    () =>
      visibleWorkspaces(hasPermission).map((ws) => {
        const tab = getFirstAccessibleTab(ws, hasPermission);
        return {
          id: ws.id,
          labelKey: ws.labelKey,
          icon: ws.icon,
          basePath: ws.basePath,
          href: tab ? `${ws.basePath}/${tab.segment}` : ws.basePath,
        };
      }),
    // POS-I107: `hasPermission` is a stable function; the list must be rebuilt when the user's
    // permissions are refreshed after login, or a workspace they lost stays in the menu.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [hasPermission, permissionsKey],
  );
}

export function DashboardLayout() {
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  // Primary sidebar collapse is persisted (hms_light layout): default = narrow icon
  // rail so the contextual workspace menu is the main nav surface. `sidebarOpen`/
  // `setSidebarOpen` keep their existing semantics but are backed by the UI store.
  const sidebarCollapsed = useUiStore((s) => s.sidebarCollapsed);
  const setSidebarCollapsed = useUiStore((s) => s.setSidebarCollapsed);
  // Treat the sidebar as expanded (labels shown) whenever it isn't collapsed OR the
  // mobile overlay is open — the collapse is a desktop-only icon rail, the mobile
  // overlay always shows full-width labels.
  const sidebarOpen = !sidebarCollapsed || mobileNavOpen;
  const setSidebarOpen = useCallback(
    (open: boolean) => setSidebarCollapsed(!open),
    [setSidebarCollapsed],
  );
  const [bellOpen, setBellOpen] = useState(false);
  const [commandOpen, setCommandOpen] = useState(false);
  const user = useAuthStore((s) => s.user);
  const tenant = useAuthStore((s) => s.tenant);
  const hasPermission = useAuthStore((s) => s.hasPermission);
  const isSubscriptionSuspended = useAuthStore((s) => s.isSubscriptionSuspended);
  const isImpersonating = useAuthStore((s) => s.isImpersonating);
  const stopImpersonation = useAuthStore((s) => s.stopImpersonation);
  const impersonationReadOnly = useAuthStore((s) => s.impersonationReadOnly);
  const setImpersonationReadOnly = useAuthStore((s) => s.setImpersonationReadOnly);
  const { theme, setTheme } = useThemeStore();
  // POS-I216: a saved brand colour is used for the store name only when it reads on the sidebar of
  // the theme in use; otherwise the name keeps the normal text colour.
  const sidebarMode =
    theme === 'dark' || (theme === 'system' && typeof window !== 'undefined' && window.matchMedia?.('(prefers-color-scheme: dark)').matches)
      ? 'dark'
      : 'light';
  const brandNameColor = readableBrandColor(tenant?.brandColor, sidebarMode);
  const logoutMutation = useLogout();
  const location = useLocation();
  const { t } = useTranslation('nav');
  const permissionsKey = user?.permissions;
  const workspaces = useSidebarWorkspaces(hasPermission, permissionsKey);
  const isSuperAdmin = user?.isSuperAdmin ?? false;

  useScreenDocumentTitle();

  // ⌘K / Ctrl+K opens the command palette (parity with hms_light).
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && (e.key === 'k' || e.key === 'K')) {
        e.preventDefault();
        setCommandOpen((v) => !v);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  // Active workspace drives the contextual secondary sidebar (app-rail model):
  // the primary sidebar switches workspaces, this column shows the workspace's tabs.
  const activeWorkspace = getWorkspaceByPath(location.pathname);
  const WsIcon = activeWorkspace?.icon;
  const contextualTabs = useMemo(
    () => (activeWorkspace ? visibleTabs(activeWorkspace, hasPermission) : []),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [activeWorkspace, hasPermission, permissionsKey],
  );

  const isEntryActive = (href: string, basePath?: string) =>
    location.pathname === href ||
    (basePath ? location.pathname === basePath || location.pathname.startsWith(`${basePath}/`) : false);

  const showPinned = (entry: PinnedEntry) => {
    if (entry.superAdminOnly && !isSuperAdmin) return false;
    return matchesPermission(entry.permission, hasPermission);
  };

  const renderPinnedEntry = (entry: PinnedEntry) => {
    const Icon = entry.icon;
    const label = t(entry.labelKey);
    const isActive = location.pathname === entry.href;
    if (entry.external) {
      return (
        <a
          key={entry.id}
          href={entry.href}
          target="_blank"
          rel="noopener noreferrer"
          onClick={() => setMobileNavOpen(false)}
          className={cn(
            'group flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium transition-all duration-200 relative',
            'text-primary-text font-semibold hover:bg-primary/10',
            !sidebarOpen && 'justify-center px-0',
          )}
          title={!sidebarOpen ? label : undefined}
        >
          <Icon className="h-[18px] w-[18px] shrink-0 transition-transform duration-200 group-hover:scale-110" />
          {sidebarOpen && (
            <>
              <span className="truncate">{label}</span>
              <ExternalLink className="ms-auto h-3.5 w-3.5 opacity-50" />
            </>
          )}
        </a>
      );
    }
    return (
      <Link
        key={entry.id}
        to={entry.href}
        onClick={() => setMobileNavOpen(false)}
        className={cn(
          'group flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium transition-all duration-200 relative',
          isActive
            ? 'bg-gradient-to-r from-primary/10 via-primary/10 to-primary/10 text-primary-text shadow-sm border border-primary/15'
            : 'text-muted-foreground hover:bg-accent hover:text-foreground',
          entry.highlight && !isActive ? 'text-primary-text font-semibold' : '',
          !sidebarOpen && 'justify-center px-0',
        )}
        title={!sidebarOpen ? label : undefined}
      >
        <Icon className={cn(
          'h-[18px] w-[18px] shrink-0 transition-transform duration-200 group-hover:scale-110',
          isActive ? 'text-primary-text' : '',
        )} />
        {sidebarOpen && <span className="truncate">{label}</span>}
        {isActive && sidebarOpen && (
          <div className="ms-auto h-2 w-2 rounded-full bg-gradient-to-r from-primary to-primary/80 animate-pulse-soft" />
        )}
      </Link>
    );
  };

  const { selectedLocationId, selectedLocationName, setLocation } = useLocationStore();
  const canViewLocations = hasPermission('locations:*') || hasPermission('locations:read');

  // Fetch locations for the switcher (only if user has locations permission)
  const { data: locationsData } = useQuery({
    queryKey: ['locations-list'],
    queryFn: () => api.get('/locations').then((r) => r.data),
    enabled: canViewLocations,
    staleTime: 60_000,
  });
  const locationsList = useMemo<LayoutLocation[]>(() => locationsData?.data ?? locationsData ?? [], [locationsData]);

  // If user has a fixed locationId from JWT, auto-sync the store and lock it.
  // Otherwise, if only 1 location exists, auto-select it (the default Main Store).
  const userFixedLocationId = user?.locationId ?? null;
  useEffect(() => {
    if (locationsList.length === 0) return;
    if (userFixedLocationId) {
      const loc = locationsList.find((l: LayoutLocation) => l.id === userFixedLocationId);
      if (loc) setLocation(loc.id, loc.name);
    } else if (locationsList.length === 1 && !selectedLocationId) {
      // Single location tenant — auto-select it so data is always scoped
      setLocation(locationsList[0].id, locationsList[0].name);
    } else if (locationsList.length > 1 && !selectedLocationId) {
      // Multi-location: auto-select the default location on first load
      const defaultLoc = locationsList.find((l: LayoutLocation) => l.isDefault);
      if (defaultLoc) setLocation(defaultLoc.id, defaultLoc.name);
    }
  }, [userFixedLocationId, locationsList, setLocation, selectedLocationId]);

  const canViewInventory = hasPermission('inventory:read') || hasPermission('inventory:*');

  // Notification bell: unread count (event-driven via WebSocket; polling as safety net)
  const queryClient = useQueryClient();
  const { data: unreadCountData } = useQuery({
    queryKey: ['notifications-unread-count'],
    queryFn: () => api.get('/notifications/unread-count').then((r) => r.data as number),
    enabled: canViewInventory && !!user,
    refetchInterval: 300_000, // 5 min safety-net; pushed via socket event
    staleTime: 300_000,
  });

  // Real-time notification count update
  const handleNewNotification = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ['notifications-unread-count'] });
    queryClient.invalidateQueries({ queryKey: ['notifications-unread'] });
  }, [queryClient]);
  useSocketEvent('notification.new', handleNewNotification);

  const { data: unreadNotifications, refetch: refetchNotifications } = useQuery({
    queryKey: ['notifications-unread'],
    queryFn: () => api.get('/notifications/unread').then((r) => r.data as { id: string; title: string; message: string; entityId: string | null; createdAt: string }[]),
    enabled: bellOpen && canViewInventory && !!user,
    staleTime: 0,
  });

  // Badge and panel share one truth: when the panel list is loaded (and not truncated at its
  // 50-row cap) the badge reflects the rows actually listed (T-250).
  const listCount = unreadNotifications?.length;
  const unreadCount: number =
    bellOpen && listCount !== undefined && listCount < 50
      ? listCount
      : typeof unreadCountData === 'number' ? unreadCountData : 0;

  const markAllReadMutation = useMutation({
    mutationFn: () => api.patch('/notifications/mark-all-read'),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications-unread-count'] });
      queryClient.invalidateQueries({ queryKey: ['notifications-unread'] });
    },
  });

  const handleMarkAllRead = () => markAllReadMutation.mutate();

  const handleMarkOneRead = async (id: string) => {
    await api.patch(`/notifications/${id}/mark-read`);
    // Refresh BOTH the panel list and the badge count so the badge doesn't revert to the
    // stale cached count after the panel closes (T-250).
    refetchNotifications();
    queryClient.invalidateQueries({ queryKey: ['notifications-unread-count'] });
  };

  // Live testMode from server — always fresh, even after toggle in Settings
  const { data: tenantData } = useQuery({
    queryKey: ['tenant-current'],
    queryFn: () => api.get('/tenant').then((r) => r.data),
    staleTime: 30_000,
  });
  const isTestMode = tenantData?.testMode ?? false;

  // WF-27 dunning / grace-period banner — overdue subscription invoice.
  const { data: dunning } = useQuery<DunningStatus>({
    queryKey: ['subscription-dunning'],
    queryFn: () => api.get('/subscription/dunning-status').then((r) => r.data),
    staleTime: 5 * 60_000,
    retry: false,
  });
  // Support mailto only (no pay link / IBAN) for callers without billing:read.
  const dunningSupportEmail =
    dunning?.paymentContact?.supportEmail ?? dunning?.supportEmail ?? 'support@clearring.com';
  const canPayBill = !!dunning?.paymentContact;
  const showDunning = !!dunning?.overdue && !dunning?.suspended && !isSubscriptionSuspended;

  // T-444 trial banner. /subscription/me needs settings:read, so only fetch for users who have it
  // (shares the ['my-subscription-usage'] cache with MyPlanUsageCard).
  const canReadSubscription = hasPermission('settings:read');
  const { data: mySubscription } = useQuery<{
    subscription: { status?: string; trialEndsAt?: string | null } | null;
    trialPhase?: 'live' | 'grace' | 'expired' | null;
    graceEndsAt?: string | null;
    trialExpired?: boolean;
  }>({
    queryKey: ['my-subscription-usage'],
    queryFn: () => api.get('/subscription/me').then((r) => r.data),
    staleTime: 5 * 60_000,
    retry: false,
    enabled: canReadSubscription,
  });
  // POS-I234: one owner of the trial maths (lib/trial.ts); the grace end and the expired phase come
  // from the server (subscription/me, else the auth/me tenant block), never from trialEndsAt + 7.
  const serverTrialPhase = mySubscription?.trialPhase ?? tenant?.trialPhase ?? null;
  const serverGraceEndsAt = mySubscription?.graceEndsAt ?? tenant?.graceEndsAt ?? null;
  const trialEnded = isSubscriptionSuspended && (mySubscription?.trialExpired ?? tenant?.trialExpired ?? false);
  const trial = getTrialPhaseState(
    mySubscription?.subscription?.status ?? (serverTrialPhase === 'live' || serverTrialPhase === 'grace' ? 'TRIAL' : undefined),
    mySubscription?.subscription?.trialEndsAt ?? tenant?.trialEndsAt,
    new Date(),
    { trialPhase: serverTrialPhase, graceEndsAt: serverGraceEndsAt },
  );
  const showTrial = !!trial && trial.phase !== 'expired' && !isSubscriptionSuspended && !showDunning;
  const trialWhatsappUrl = dunning?.paymentContact?.whatsappUrl ?? null;

  // Close bell dropdown on route change
  useEffect(() => {
    setBellOpen(false);
  }, [location.pathname]);

  const toggleTheme = () => {
    setTheme(theme === 'dark' ? 'light' : 'dark');
  };

  /** Config-driven breadcrumb: "Workspace › Tab" (or the page name). */
  const getBreadcrumb = (pathname: string): string => {
    const topLevel: Record<string, string> = {
      '/dashboard': t('home'),
      '/reports': t('reports'),
      '/settings': t('settings'),
      '/store-guide': t('storeGuide'),
      '/platform': t('platform'),
    };
    if (topLevel[pathname]) return topLevel[pathname];

    const ws = getWorkspaceByPath(pathname);
    if (ws) {
      const tab = ws.tabs.find((tb) => {
        const abs = `${ws.basePath}/${tb.segment}`;
        return pathname === abs || pathname.startsWith(`${abs}/`);
      });
      const wsLabel = t(ws.labelKey);
      return tab ? `${wsLabel} › ${t(tab.labelKey)}` : wsLabel;
    }
    return pathname.replace('/', '').replace(/-/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase());
  };

  return (
    <RecordViewerProvider>
    <div className="flex h-screen overflow-hidden bg-background print:block print:h-auto print:overflow-visible print:bg-background">
      {/* Mobile overlay backdrop */}
      {mobileNavOpen && (
        <div className="fixed inset-0 bg-scrim/50 z-40 lg:hidden print:hidden" onClick={() => setMobileNavOpen(false)} />
      )}
      {/* Sidebar */}
      <aside
        className={cn(
          'print:hidden',
          'flex flex-col glass sidebar-gradient transition-all duration-300',
          // Desktop: in-flow relative sidebar, collapsible
          'lg:relative lg:z-auto lg:translate-x-0 lg:flex',
          // Mobile: fixed overlay — use display:none when closed so it never
          // reserves width in the flex layout (translate alone is unreliable
          // on some mobile browsers when parent has overflow:hidden)
          'fixed inset-y-0 start-0 z-50',
          'border-e',
          mobileNavOpen ? 'flex' : 'hidden lg:flex',
          sidebarOpen ? 'w-64' : 'w-64 lg:w-[68px]',
        )}
      >
        {/* Logo */}
        <div className="flex h-16 items-center border-b border-border/50 px-3">
          {sidebarOpen ? (
            <div className="flex items-center justify-between gap-2 w-full animate-fade-in">
              <div className="flex min-w-0 items-center gap-2.5">
                {tenant?.brandLogoUrl ? (
                  <img
                    src={tenant.brandLogoUrl}
                    alt={tenant.name}
                    className="h-9 w-9 shrink-0 rounded-xl object-contain bg-background/10 border border-border/30"
                  />
                ) : (
                  <div
                    className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl text-primary-foreground shadow-lg animate-gradient"
                    style={tenant?.brandColor
                      ? { background: tenant.brandColor }
                      : undefined}
                    {...(!tenant?.brandColor && { className: "flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-primary via-primary/90 to-primary/80 text-primary-foreground shadow-lg shadow-primary/25 animate-gradient" })}
                  >
                    <ShoppingCart className="h-5 w-5" />
                  </div>
                )}
                <div className="min-w-0 flex-1 overflow-hidden">
                  {/* POS-I230: a very long business name stops at the sidebar edge with an ellipsis; the title shows it whole */}
                  <p
                    className="truncate text-sm font-bold"
                    title={tenant?.name || 'POS System'}
                    style={brandNameColor ? { color: brandNameColor } : undefined}
                  >
                    {!tenant?.brandColor && (
                      <span className="block truncate bg-gradient-to-r from-primary to-primary/80 bg-clip-text text-transparent">
                        {tenant?.name || 'POS System'}
                      </span>
                    )}
                    {tenant?.brandColor && <span className="block truncate">{tenant?.name || 'POS System'}</span>}
                  </p>
                  <p
                    className="truncate text-[10px] text-muted-foreground"
                    title={tenant?.brandTagline || tenant?.slug || 'pos'}
                  >
                    {tenant?.brandTagline || tenant?.slug || 'pos'}
                  </p>
                </div>
              </div>
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8 shrink-0 hover:bg-primary/10"
                onClick={() => setSidebarOpen(false)}
                aria-label={t('collapseSidebar')}
                title={t('collapseSidebar')}
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
            </div>
          ) : (
            <Button
              variant="ghost"
              size="icon"
              className="mx-auto h-9 w-9"
              onClick={() => setSidebarOpen(true)}
              aria-label={t('expandSidebar')}
              title={t('expandSidebar')}
            >
              {tenant?.brandLogoUrl ? (
                <img src={tenant.brandLogoUrl} alt={tenant.name} className="h-6 w-6 rounded object-contain" />
              ) : (
                <Menu className="h-5 w-5" />
              )}
            </Button>
          )}
        </div>

        {/* Nav items */}
        <nav className="flex-1 overflow-y-auto py-3 px-2.5 space-y-0.5">
          {/* Pinned: Home + POS terminal */}
          {PINNED_TOP.filter(showPinned).map(renderPinnedEntry)}

          {/* Workspaces */}
          {workspaces.length > 0 && (
            <>
              {sidebarOpen ? (
                <p className="px-3 pt-4 pb-1 text-[10px] font-bold uppercase tracking-widest text-muted-foreground/50">
                  {t('workspaces')}
                </p>
              ) : (
                <div className="my-2 border-t border-border/40" />
              )}
              {workspaces.map((ws) => {
                const Icon = ws.icon;
                const isActive = isEntryActive(ws.href, ws.basePath);
                return (
                  <Link
                    key={ws.id}
                    to={ws.href}
                    onClick={() => setMobileNavOpen(false)}
                    className={cn(
                      'group flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium transition-all duration-200 relative',
                      isActive
                        ? 'bg-gradient-to-r from-primary/10 via-primary/10 to-primary/10 text-primary-text shadow-sm border border-primary/15'
                        : 'text-muted-foreground hover:bg-accent hover:text-foreground',
                      !sidebarOpen && 'justify-center px-0',
                    )}
                    title={!sidebarOpen ? t(ws.labelKey) : undefined}
                  >
                    <Icon className={cn(
                      'h-[18px] w-[18px] shrink-0 transition-transform duration-200 group-hover:scale-110',
                      isActive ? 'text-primary-text' : '',
                    )} />
                    {sidebarOpen && <span className="truncate">{t(ws.labelKey)}</span>}
                    {isActive && sidebarOpen && (
                      <div className="ms-auto h-2 w-2 rounded-full bg-gradient-to-r from-primary to-primary/80 animate-pulse-soft" />
                    )}
                  </Link>
                );
              })}
            </>
          )}

          {/* Reports hub */}
          {showPinned(PINNED_REPORTS) && (
            <>
              {sidebarOpen ? (
                <p className="px-3 pt-4 pb-1 text-[10px] font-bold uppercase tracking-widest text-muted-foreground/50">
                  {t('insights')}
                </p>
              ) : (
                <div className="my-2 border-t border-border/40" />
              )}
              {renderPinnedEntry(PINNED_REPORTS)}
            </>
          )}

          {/* System */}
          {PINNED_SYSTEM.filter(showPinned).length > 0 && (
            <>
              {sidebarOpen ? (
                <p className="px-3 pt-4 pb-1 text-[10px] font-bold uppercase tracking-widest text-muted-foreground/50">
                  {t('system')}
                </p>
              ) : (
                <div className="my-2 border-t border-border/40" />
              )}
              {PINNED_SYSTEM.filter(showPinned).map(renderPinnedEntry)}
            </>
          )}
        </nav>

        {/* User section */}
        <div className="border-t border-border/50 p-2.5">
          <div className={cn(
            'flex items-center gap-2.5 rounded-xl p-2',
            sidebarOpen ? '' : 'justify-center',
          )}>
            <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-success to-success/80 text-xs font-bold text-success-foreground shadow-md shadow-success/20">
              {user?.firstName?.[0]}{user?.lastName?.[0]}
            </div>
            {sidebarOpen && (
              <div className="flex-1 min-w-0">
                <p className="truncate text-sm font-semibold">
                  {user?.firstName} {user?.lastName}
                </p>
                <p className="truncate text-[11px] text-muted-foreground">
                  {user?.roles?.[0] || 'user'}
                </p>
              </div>
            )}
          </div>
          {sidebarOpen && (
            <div className="mt-1.5 flex gap-1">
              <Button
                variant="ghost"
                size="sm"
                className="flex-1 h-8 text-xs rounded-lg"
                onClick={toggleTheme}
              >
                {theme === 'dark' ? (
                  <Sun className="me-1.5 h-3.5 w-3.5" />
                ) : (
                  <Moon className="me-1.5 h-3.5 w-3.5" />
                )}
                {t('theme')}
              </Button>
              <Button
                variant="ghost"
                size="sm"
                className="flex-1 h-8 text-xs rounded-lg text-destructive-text hover:text-destructive-text hover:bg-destructive/10"
                onClick={() => logoutMutation.mutate()}
              >
                <LogOut className="me-1.5 h-3.5 w-3.5" />
                {t('logout')}
              </Button>
            </div>
          )}
        </div>
      </aside>

      {/* Contextual secondary sidebar — the active workspace's tabs as a vertical
          list (hms_light workspace model). Desktop only; mobile uses the overlay
          sidebar + bottom nav. Hidden on non-workspace (pinned) pages. */}
      {activeWorkspace && WsIcon && contextualTabs.length > 0 && (
        <aside className="hidden lg:flex print:hidden w-56 shrink-0 flex-col border-e border-border/60 glass sidebar-gradient">
          <div className="flex h-16 items-center gap-2.5 border-b border-border/50 px-4">
            <WsIcon className="h-5 w-5 shrink-0 text-primary-text" />
            <p className="truncate text-sm font-bold">{t(activeWorkspace.labelKey)}</p>
          </div>
          <nav className="flex-1 overflow-y-auto py-3 px-2.5 space-y-0.5">
            {contextualTabs.map((tab) => {
              const abs = `${activeWorkspace.basePath}/${tab.segment}`;
              const active =
                location.pathname === abs || location.pathname.startsWith(`${abs}/`);
              const TabIcon = tab.icon;
              return (
                <Link
                  key={tab.key}
                  to={abs}
                  className={cn(
                    'group flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium transition-all duration-200',
                    active
                      ? 'bg-gradient-to-r from-primary/10 via-primary/10 to-primary/10 text-primary-text shadow-sm border border-primary/15'
                      : 'text-muted-foreground hover:bg-accent hover:text-foreground',
                  )}
                >
                  {TabIcon && (
                    <TabIcon
                      className={cn(
                        'h-[18px] w-[18px] shrink-0 transition-transform duration-200 group-hover:scale-110',
                        active ? 'text-primary-text' : '',
                      )}
                    />
                  )}
                  <span className="truncate">{t(tab.labelKey)}</span>
                  {active && (
                    <div className="ms-auto h-2 w-2 rounded-full bg-gradient-to-r from-primary to-primary/80 animate-pulse-soft" />
                  )}
                </Link>
              );
            })}
          </nav>
        </aside>
      )}

      {/* Floating gradient orbs — rendered at root level so modals aren't affected */}
      <div className="pointer-events-none fixed inset-0 overflow-hidden z-0 print:hidden">
        <div className="absolute -top-32 -end-32 h-[500px] w-[500px] rounded-full bg-gradient-to-br from-primary/[0.07] to-primary/[0.05] blur-3xl animate-drift" />
        <div className="absolute -bottom-40 -start-40 h-[600px] w-[600px] rounded-full bg-gradient-to-tr from-primary/[0.05] to-primary/[0.04] blur-3xl animate-drift-reverse" />
        <div className="absolute top-1/3 end-1/4 h-64 w-64 rounded-full bg-gradient-to-br from-info/[0.04] to-info/[0.03] blur-2xl animate-float-slow" />
      </div>

      {/* Main content */}
      <main className="flex-1 overflow-auto bg-gradient-mesh print:overflow-visible print:bg-none">
        {/* Impersonation banner — sticky, high z-index */}
        {isImpersonating && (
          <div className="sticky top-0 z-40 print:hidden flex flex-wrap items-center gap-3 bg-destructive px-4 py-3 text-sm font-medium text-destructive-foreground shadow-lg">
            <Shield className="h-5 w-5 shrink-0" />
            <div className="flex-1 min-w-[12rem]">
              <span className="font-bold">Impersonating</span>
              {' — '}You are viewing as <span className="font-bold break-words">{tenant?.name}</span> ({user?.email}).
              {impersonationReadOnly ? ' Read-only mode is ON.' : ' ⚠️ Edits are LIVE.'}
            </div>
            <button
              onClick={() => setImpersonationReadOnly(!impersonationReadOnly)}
              className={`shrink-0 inline-flex items-center gap-1.5 rounded-md px-3 py-1 text-xs font-semibold transition-colors ${
                impersonationReadOnly ? 'bg-background/20 hover:bg-background/30' : 'bg-warning/30 text-warning-text hover:bg-warning/20'
              }`}
              title={impersonationReadOnly ? 'Read-only mode is on — click to allow edits' : 'Edits are live — click to lock to read-only'}
            >
              {impersonationReadOnly ? <Lock className="h-3.5 w-3.5" /> : <Unlock className="h-3.5 w-3.5" />}
              {impersonationReadOnly ? 'Read-only' : 'Editing live'}
            </button>
            <button
              onClick={() => {
                stopImpersonation();
                window.location.href = '/platform';
              }}
              className="shrink-0 rounded-md bg-background/20 px-3 py-2 text-xs font-semibold hover:bg-background/30 transition-colors"
            >
              Exit
            </button>
          </div>
        )}
        {/* Subscription suspended banner — always visible, non-dismissible */}
        {isSubscriptionSuspended && (
          <div className="sticky top-0 z-30 print:hidden flex flex-wrap items-center gap-x-3 gap-y-2 bg-destructive px-4 py-3 text-sm font-medium text-destructive-foreground shadow-lg">
            <AlertOctagon className="h-5 w-5 shrink-0 animate-pulse" />
            <div className="min-w-[14rem] flex-1">
              <span className="font-bold">{trialEnded ? t('trialEndedTitle') : t('suspendedBannerTitle')}</span>
              {' — '}{trialEnded ? t('trialEndedBody') : t('suspendedBannerBody')}
            </div>
            {dunning?.paymentContact?.whatsappUrl && (
              <a
                href={dunning.paymentContact.whatsappUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="shrink-0 inline-flex items-center gap-1.5 max-md:min-h-11 rounded-md bg-background px-3 py-2 text-xs font-semibold text-destructive-text hover:bg-background/90 transition-colors"
              >
                <MessageCircle className="h-3.5 w-3.5" />
                {trialEnded ? t('trialContactCta') : t('payOnWhatsApp')}
              </a>
            )}
            {dunning?.overdue && !canPayBill && (
              <span className="shrink-0 text-xs">{t('askOwnerToSettle')}</span>
            )}
            <a
              href={`mailto:${dunningSupportEmail}`}
              className="shrink-0 max-md:min-h-11 max-md:inline-flex max-md:items-center rounded-md bg-background/20 px-3 py-2 text-xs font-semibold hover:bg-background/30 transition-colors"
            >
              {t('contactSupport')}
            </a>
          </div>
        )}
        {/* POS-I185: owner e-mail verification (own state from /auth/me; the lock itself is a server rule) */}
        <EmailVerifyBanner />
        {/* WF-27 subscription dunning / grace-period banner */}
        {showDunning && (
          <div className="sticky top-0 z-20 print:hidden flex flex-wrap items-center gap-x-3 gap-y-2 bg-warning px-4 py-2.5 text-sm font-medium text-warning-foreground shadow-sm">
            <AlertOctagon className="h-4 w-4 shrink-0" />
            <div className="min-w-[14rem] flex-1">
              <strong>{t('dunningOverdueTitle')}</strong>
              {' — '}{t('dunningOverdueBody', { days: dunning?.daysOverdue ?? 0 })}
              {typeof dunning?.daysUntilSuspension === 'number' && dunning.daysUntilSuspension > 0
                ? `. ${t('dunningSuspendWarn', { days: dunning.daysUntilSuspension })}`
                : '.'}
            </div>
            {dunning?.paymentContact?.whatsappUrl && (
              <a
                href={dunning.paymentContact.whatsappUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="shrink-0 inline-flex items-center gap-1.5 max-md:min-h-11 rounded-md bg-warning-foreground px-3 py-2 text-xs font-semibold text-warning hover:bg-warning-foreground/90 transition-colors"
              >
                <MessageCircle className="h-3.5 w-3.5" />
                {t('payOnWhatsApp')}
              </a>
            )}
            {canPayBill ? (
              <Link
                to="/admin/billing"
                className="shrink-0 max-md:min-h-11 max-md:inline-flex max-md:items-center rounded-md bg-warning-foreground px-3 py-2 text-xs font-semibold text-warning hover:bg-warning-foreground/90 transition-colors"
              >
                {t('resolvePay')}
              </Link>
            ) : (
              <>
                <span className="shrink-0 text-xs">{t('askOwnerToSettle')}</span>
                <a
                  href={`mailto:${dunningSupportEmail}`}
                  className="shrink-0 max-md:min-h-11 max-md:inline-flex max-md:items-center rounded-md bg-warning-foreground px-3 py-2 text-xs font-semibold text-warning hover:bg-warning-foreground/90 transition-colors"
                >
                  {t('contactSupport')}
                </a>
              </>
            )}
          </div>
        )}
        {/* T-444 free-trial banner — informational (info tone, non-sticky); distinct from dunning/suspended/test-mode.
            POS-I234: live = all modules included; grace = warning with the server's end date and a WhatsApp CTA. */}
        {showTrial && trial && trial.phase === 'live' && (
          <div
            role="status"
            className={`print:hidden flex flex-wrap items-center gap-x-3 gap-y-1 border-b border-info/30 px-4 py-2 text-sm text-info-text ${
              trial.daysLeft <= 3 ? 'bg-info/20' : 'bg-info/10'
            }`}
          >
            <Clock className="h-4 w-4 shrink-0" />
            <span className="flex-1 min-w-0">
              <strong>{t('trialBannerTitle')}</strong>
              {' — '}
              {trial.daysLeft === 0
                ? t('trialBannerEndsToday')
                : t('trialBannerDaysLeft', { count: trial.daysLeft })}
            </span>
            <Link to="/settings?tab=team#my-plan" className="shrink-0 font-semibold underline underline-offset-2 hover:opacity-80">
              {t('trialBannerCta')}
            </Link>
          </div>
        )}
        {showTrial && trial && trial.phase === 'grace' && (
          <div
            role="status"
            className="print:hidden flex flex-wrap items-center gap-x-3 gap-y-2 border-b border-warning/40 bg-warning px-4 py-2.5 text-sm font-medium text-warning-foreground"
          >
            <Clock className="h-4 w-4 shrink-0" />
            <span className="min-w-[14rem] flex-1">
              <strong>{t('trialGraceTitle')}</strong>
              {' — '}
              {trial.graceEndsAt ? (
                // the date sits inside <bdi> so it keeps its direction in Urdu (RTL)
                (() => {
                  const [before, after] = t('trialGraceBody', { date: ' ' }).split(' ');
                  return (
                    <>
                      {before}
                      <bdi>{formatDateOnly(trial.graceEndsAt as Date)}</bdi>
                      {after}
                    </>
                  );
                })()
              ) : (
                t('trialBannerExpired')
              )}
            </span>
            {trialWhatsappUrl ? (
              <a
                href={trialWhatsappUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="shrink-0 inline-flex items-center gap-1.5 max-md:min-h-11 rounded-md bg-background px-3 py-2 text-xs font-semibold text-warning-text hover:bg-background/90 transition-colors"
              >
                <MessageCircle className="h-3.5 w-3.5" />
                {t('trialContactCta')}
              </a>
            ) : (
              <Link to="/settings?tab=team#my-plan" className="shrink-0 font-semibold underline underline-offset-2 hover:opacity-80">
                {t('trialBannerCta')}
              </Link>
            )}
          </div>
        )}
        {isTestMode && !isSubscriptionSuspended && (
          <div className="sticky top-0 z-20 print:hidden flex items-center gap-2 bg-warning px-4 py-2 text-sm font-medium text-warning-foreground shadow-sm">
            <FlaskConical className="h-4 w-4 shrink-0" />
            <span>
              <Trans
                i18nKey="testModeBanner"
                ns="nav"
                components={{
                  0: <strong />,
                  1: <Link to="/settings" className="underline underline-offset-2 hover:opacity-80" />,
                }}
              />
            </span>
          </div>
        )}
        {isTestMode && isSubscriptionSuspended && (
          <div className="flex items-center gap-2 print:hidden bg-warning px-4 py-2 text-sm font-medium text-warning-foreground">
            <FlaskConical className="h-4 w-4 shrink-0" />
            <span><strong>{t('testModeActive')}</strong></span>
          </div>
        )}
        <div className="p-6 pb-20 lg:pb-6 page-enter bg-dots print:p-0 print:bg-none">
          {/* Mobile menu button — always visible on mobile */}
          <button
            className="lg:hidden print:hidden mb-4 p-2 -ms-2 rounded-md hover:bg-accent"
            onClick={() => setMobileNavOpen((v) => !v)}
            aria-label={t('openMenu')}
          >
            <Menu className="h-5 w-5" />
          </button>
          {/* Location context bar — shown when any locations exist */}
          {canViewLocations && locationsList.length >= 1 && (
            <div className="mb-4 flex items-center gap-2 print:hidden rounded-xl border border-border/60 bg-card/80 px-4 py-2.5 shadow-sm backdrop-blur-sm">
              <MapPin className="h-4 w-4 text-primary-text shrink-0" />
              <label htmlFor="header-location-picker" className="text-xs font-medium text-muted-foreground">{t('locationLabel')}</label>
              {userFixedLocationId ? (
                // Cashier: fixed location, no switcher
                <span className="text-xs font-semibold text-foreground">{selectedLocationName ?? 'Loading…'}</span>
              ) : (
                // Manager: dropdown switcher
                <div className="relative">
                  <Dropdown
                    value={selectedLocationId ?? ''}
                    onChange={(e) => {
                      const val: string = e.value;
                      if (!val) {
                        setLocation(null, null);
                      } else {
                        const loc = locationsList.find((l: LayoutLocation) => l.id === val);
                        setLocation(val, loc?.name ?? val);
                      }
                    }}
                    options={[
                      { label: 'All Locations', value: '' },
                      ...locationsList.map((loc: LayoutLocation) => ({ label: loc.name, value: loc.id })),
                    ]}
                    optionLabel="label"
                    optionValue="value"
                    inputId="header-location-picker"
                    ariaLabel={t('locationLabel')}
                    aria-label={t('locationLabel')}
                    className="p-dropdown-sm text-xs font-medium"
                  />
                </div>
              )}
              {selectedLocationId && !userFixedLocationId && (
                <button
                  onClick={() => setLocation(null, null)}
                  className="ms-1 text-[10px] text-muted-foreground hover:text-foreground underline"
                >
                  {t('clearLocation')}
                </button>
              )}
              <span className="ms-auto text-[10px] text-muted-foreground">
                {selectedLocationId
                  ? t('viewingLocation', { name: selectedLocationName, defaultValue: 'Viewing: {{name}}' })
                  : locationsList.length > 1
                  ? t('viewingAllLocations', { n: locationsList.length, defaultValue: 'Viewing all {{n}} locations' })
                  : ''}
              </span>
            </div>
          )}
          {/* Breadcrumb + Notification Bell */}
          <div className="mb-4 px-1 py-1.5 border-b border-border/50 flex items-center justify-between print:hidden">
            <nav className="flex items-center gap-1.5 text-xs text-muted-foreground">
              <Home className="h-3 w-3" />
              <ChevronRight className="h-3 w-3" />
              <span className="font-medium text-foreground capitalize">
                {getBreadcrumb(location.pathname)}
              </span>
            </nav>
            {/* Language switcher + Notification Bell */}
            <div className="flex items-center gap-2">
              <button
                onClick={() => setCommandOpen(true)}
                className="hidden sm:flex items-center gap-2 rounded-lg border border-border/70 bg-muted/40 px-2.5 py-1.5 text-xs text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                aria-label={t('paletteAria', { defaultValue: 'Open command palette' })}
                title={t('paletteAria', { defaultValue: 'Command palette' })}
              >
                <Search className="h-3.5 w-3.5" />
                <span className="hidden md:inline">{t('search', { defaultValue: 'Search' })}</span>
                <kbd className="rounded border border-border bg-background px-1 font-mono text-[10px] leading-none">⌘K</kbd>
              </button>
              <LanguageSwitcher size="sm" />
              {/* Notification Bell */}
            {canViewInventory && (
              <div className="relative">
                <button
                  onClick={() => {
                    if (!bellOpen) {
                      queryClient.invalidateQueries({ queryKey: ['notifications-unread-count'] });
                      queryClient.invalidateQueries({ queryKey: ['notifications-unread'] });
                    }
                    setBellOpen((v) => !v);
                  }}
                  className="relative rounded-lg p-1.5 hover:bg-accent transition-colors"
                  aria-label={t('notifications')}
                >
                  <Bell className="h-4 w-4 text-muted-foreground" />
                  {unreadCount > 0 && (
                    <span className="absolute -top-0.5 -end-0.5 flex h-4 w-4 items-center justify-center rounded-full bg-destructive text-[9px] font-bold text-destructive-foreground leading-none">
                      {unreadCount > 99 ? '99+' : unreadCount}
                    </span>
                  )}
                </button>
                {bellOpen && (
                  <div className="absolute end-0 top-8 z-50 w-80 rounded-xl border bg-card shadow-xl animate-fade-in-up">
                    <div className="flex items-center justify-between border-b px-4 py-2.5">
                      <p className="text-sm font-semibold">{t('notifications')}</p>
                      <div className="flex items-center gap-2">
                        {unreadCount > 0 && (
                          <button
                            onClick={handleMarkAllRead}
                            className="text-xs text-primary-text hover:text-primary-text"
                          >
                            Mark all read
                          </button>
                        )}
                        <button onClick={() => setBellOpen(false)}>
                          <X className="h-3.5 w-3.5 text-muted-foreground" />
                        </button>
                      </div>
                    </div>
                    <div className="max-h-80 overflow-y-auto">
                      {!unreadNotifications || unreadNotifications.length === 0 ? (
                        <p className="px-4 py-6 text-center text-sm text-muted-foreground">No new notifications</p>
                      ) : (
                        unreadNotifications.map((n) => (
                          <div
                            key={n.id}
                            className="flex items-start gap-3 border-b px-4 py-3 hover:bg-accent/30 transition-colors"
                          >
                            <div className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-warning/10">
                              <Bell className="h-3.5 w-3.5 text-warning-text" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-xs font-semibold truncate">{n.title}</p>
                              <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">{n.message}</p>
                              <p className="text-[10px] text-muted-foreground/60 mt-1">
                                {new Date(n.createdAt).toLocaleDateString(undefined, { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}
                              </p>
                            </div>
                            <button
                              onClick={() => handleMarkOneRead(n.id)}
                              className="shrink-0 rounded p-0.5 hover:bg-accent"
                              title="Mark as read"
                            >
                              <X className="h-3 w-3 text-muted-foreground" />
                            </button>
                          </div>
                        ))
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}
            </div>
          </div>
          <Outlet />
        </div>
      </main>
      {/* Mobile bottom navigation */}
      <nav className="lg:hidden print:hidden fixed bottom-0 inset-x-0 z-50 bg-background border-t border-border flex items-center justify-around h-14 px-2">
        <Link to="/dashboard" className="flex flex-col items-center gap-0.5 text-xs text-muted-foreground hover:text-foreground p-2">
          <LayoutDashboard className="h-5 w-5" />
          <span>Home</span>
        </Link>
        <Link to="/pos" className="flex flex-col items-center gap-0.5 text-xs text-muted-foreground hover:text-foreground p-2">
          <ShoppingCart className="h-5 w-5" />
          <span>POS</span>
        </Link>
        {hasPermission('restaurant:read') && (
          <Link to="/restaurant/floor" className="flex flex-col items-center gap-0.5 text-xs text-muted-foreground hover:text-foreground p-2">
            <UtensilsCrossed className="h-5 w-5" />
            <span>Tables</span>
          </Link>
        )}
        <button
          onClick={() => setMobileNavOpen((v) => !v)}
          className="flex flex-col items-center gap-0.5 text-xs text-muted-foreground hover:text-foreground p-2"
        >
          <Menu className="h-5 w-5" />
          <span>Menu</span>
        </button>
      </nav>

      <CommandPalette open={commandOpen} onOpenChange={setCommandOpen} />
    </div>
    </RecordViewerProvider>
  );
}

